import React, { useEffect } from 'react'
import {
  Card,
  Form,
  Input,
  Row,
  Col,
  Button,
  Switch,
  Skeleton,
  DatePicker,
  Select,
  Divider,
} from 'antd'
import { useParams, useHistory } from 'react-router-dom'
import moment from 'moment'
import { useDispatch } from 'react-redux'
import { hardwareValidation } from 'validations'
import { AmountInput } from 'components/elements'
import {
  addHardware,
  getHardware,
  getOrganizationSelectorOptions,
  updateHardware,
} from 'store/hardwareSlice/hardwareActions'
import { useHardwareSelector } from 'store/hardwareSlice/hardwareReducer'

const { TextArea } = Input

const initialValues = {
  OrganizationId: '',
  CustomerOwned: false,
  Notes: '',
  HardwareType: '',
  ManufacturerName: '',
  DeviceUUID: '',
  DeviceName: '',
  CardReaderSerialNumber: '',
  CardReaderFirmwareVersion: '',
  CardReaderType: '',
  OperatingSystemVersion: '',
  ExtensiaAppVersion: '',
  BluetoothCode: '',
  UseCustomAmount: false,
  CustomAmount: '',
  MDMDeviceName: '',
  DeploymentDate: null,
  DeviceLocation: '',
  LastCheckInTime: null,
  ExtensiaLastLogin: null,
}

const AddOrEditHardware = () => {
  const dispatch = useDispatch()
  const history = useHistory()
  const params = useParams()
  const { organizationId, hardwareId } = params
  const isEdit = organizationId && hardwareId
  const { loading, organizationOptions } = useHardwareSelector()
  const { fetchOrganizationOptions, addOrUpdate } = loading
  const [form] = Form.useForm()

  Form.useWatch('CustomerOwned', form)
  Form.useWatch('UseCustomAmount', form)
  Form.useWatch('DeploymentDate', form)
  useEffect(() => {
    dispatch(getOrganizationSelectorOptions())
  }, [dispatch])

  useEffect(() => {
    if (isEdit) {
      dispatch(getHardware(organizationId, hardwareId, form))
    }
  }, [dispatch, isEdit, organizationId, hardwareId, form])

  const navigation = () => {
    form.resetFields()
    history.push('/hardware')
  }

  const onFinish = async (values) => {
    const {
      OrganizationId,
      CustomerOwned,
      Notes,
      HardwareType,
      ManufacturerName,
      DeviceUUID,
      DeviceName,
      CardReaderSerialNumber,
      CardReaderFirmwareVersion,
      CardReaderType,
      OperatingSystemVersion,
      ExtensiaAppVersion,
      BluetoothCode,
      UseCustomAmount,
      CustomAmount,
      MDMDeviceName,
      DeploymentDate,
      DeviceLocation,
    } = values

    const payload = {
      CustomerOwned,
      Notes,
      HardwareType,
      ManufacturerName,
      DeviceUUID,
      DeviceName,
      CardReaderSerialNumber,
      CardReaderFirmwareVersion,
      CardReaderType,
      OperatingSystemVersion,
      ExtensiaAppVersion,
      BluetoothCode,
      UseCustomAmount,
      CustomAmount,
      MDMDeviceName,
      DeploymentDate: moment(DeploymentDate).format('YYYY-MM-DD'),
      DeviceLocation,
    }
    if (isEdit) {
      payload.OrganizationId = organizationId
      payload.HardwareId = hardwareId
      dispatch(updateHardware(payload, navigation))
    } else {
      const OrganizationName = organizationOptions.find(
        (item) => item.value === OrganizationId,
      ).label
      payload.OrganizationId = OrganizationId
      payload.OrganizationName = OrganizationName
      dispatch(addHardware(payload, navigation))
    }
  }

  const buttonText = false
    ? isEdit
      ? 'Updating Hardware'
      : 'Adding Hardware'
    : hardwareId
    ? 'Update Hardware'
    : 'Add Hardware'

  return (
    <Card
      className="header-solid"
      bordered={false}
      title={[
        <>
          <h6 className="mb-0 t-text-2xl">
            {isEdit ? 'Update Hardware' : 'Add Hardware'}
          </h6>
          <p className="font-regular">Enter Information</p>
        </>,
      ]}
    >
      <Skeleton
        loading={fetchOrganizationOptions}
        active
        paragraph={{ rows: 18 }}
      >
        <Form
          form={form}
          layout="vertical"
          key={0}
          onFinish={onFinish}
          name="Hardware Form"
          initialValues={initialValues}
          scrollToFirstError
        >
          <Row gutter={[24, 0]}>
            <Col xs={24} sm={24} md={12} xl={12}>
              <Form.Item
                name="OrganizationId"
                label="Organization*"
                colon={false}
                rules={[hardwareValidation]}
              >
                <Select
                  placeholder="Select Organization"
                  style={{ width: '100%' }}
                  showSearch
                  optionFilterProp="children"
                  disabled={isEdit}
                >
                  {[...organizationOptions]
                    .filter((x) => x.status === 'Active')
                    .sort((a, b) => a.label.localeCompare(b.label))
                    .map((item, index) => (
                      <Select.Option value={item.value} key={index}>
                        {item.label}
                      </Select.Option>
                    ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={12} xl={12}>
              <Form.Item
                name="CustomerOwned"
                label="Customer Owned"
                colon={false}
                rules={[hardwareValidation]}
              >
                <Switch checked={form.getFieldValue('CustomerOwned')} />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                name="Notes"
                label="Notes"
                colon={false}
                rules={[hardwareValidation]}
              >
                <TextArea
                  placeholder="Notes"
                  autoSize={{ minRows: 3, maxRows: 5 }}
                />
              </Form.Item>
            </Col>
            <Divider />
            {isEdit && (
              <>
                <Col xs={24} sm={24} md={12} xl={12}>
                  <Form.Item
                    name="ExtensiaLastLogin"
                    label="Extensia Last Login"
                    colon={false}
                    // rules={[hardwareValidation]}
                  >
                    <Input disabled={isEdit} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={12} xl={12}>
                  <Form.Item
                    name="LastCheckInTime"
                    label="Last Check In Time"
                    colon={false}
                    // rules={[hardwareValidation]}
                  >
                    <Input disabled={isEdit} />
                  </Form.Item>
                </Col>
              </>
            )}
            <Col xs={24} sm={24} md={12} xl={12}>
              <Form.Item
                name="HardwareType"
                label="Hardware Type*"
                colon={false}
                rules={[hardwareValidation]}
              >
                <Select
                  placeholder="Select Hardware Type "
                  style={{ width: '100%' }}
                >
                  {['Kiosk', 'Tv'].map((item, index) => (
                    <Select.Option value={item} key={index}>
                      {item}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={12} xl={12}>
              <Form.Item
                name="ManufacturerName"
                label="Manufacturer Name*"
                colon={false}
                rules={[hardwareValidation]}
              >
                <Input placeholder="Enter Manufacturer Name" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={12} xl={12}>
              <Form.Item
                name="DeviceUUID"
                label="Device UUID*"
                colon={false}
                rules={[hardwareValidation]}
              >
                <Input placeholder="Enter Device UUID" disabled={isEdit} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={12} xl={12}>
              <Form.Item
                name="DeviceName"
                label="Device Name*"
                colon={false}
                rules={[hardwareValidation]}
              >
                <Input placeholder="Enter Device Name" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={12} xl={12}>
              <Form.Item
                name="CardReaderSerialNumber"
                label="Card Reader Serial Number*"
                colon={false}
                rules={[hardwareValidation]}
              >
                <Input
                  placeholder="Enter Card Reader Serial Number"
                  disabled={isEdit}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={12} xl={12}>
              <Form.Item
                name="CardReaderFirmwareVersion"
                label="Card Reader Firmware Versionr*"
                colon={false}
                rules={[hardwareValidation]}
              >
                <Input
                  placeholder="Enter Card Reader Firmware Version"
                  disabled={isEdit}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={12} xl={12}>
              <Form.Item
                name="CardReaderType"
                label="Card Reader Type*"
                colon={false}
                rules={[hardwareValidation]}
              >
                <Select
                  placeholder="Card Reader Type"
                  style={{ width: '100%' }}
                >
                  {[{ label: 'Stripe Reader M2', value: 'stripe_m2' }].map(
                    (item, index) => (
                      <Select.Option value={item?.value} key={index}>
                        {item?.label}
                      </Select.Option>
                    ),
                  )}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={12} xl={12}>
              <Form.Item
                name="BluetoothCode"
                label="BluetoothCode"
                colon={false}
                rules={[hardwareValidation]}
              >
                <Input placeholder="Enter Bluetooth Code" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={12} xl={12}>
              <Form.Item
                name="OperatingSystemVersion"
                label="Operating System Version*"
                colon={false}
                rules={[hardwareValidation]}
              >
                <Input
                  placeholder="Enter Operating System Version"
                  disabled={isEdit}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={12} xl={12}>
              <Form.Item
                name="ExtensiaAppVersion"
                label="Extensia App Version*"
                colon={false}
                rules={[hardwareValidation]}
              >
                <Input
                  placeholder="Enter Extensia App Version"
                  disabled={isEdit}
                />
              </Form.Item>
            </Col>
            <Divider />
            <Col xs={24} sm={24} md={12} xl={12}>
              <Form.Item
                name="UseCustomAmount"
                label="Use Custom Amount"
                colon={false}
                rules={[hardwareValidation]}
              >
                <Switch checked={form.getFieldValue('UseCustomAmount')} />
              </Form.Item>
            </Col>
            {form.getFieldValue('UseCustomAmount') && (
              <Col xs={24} sm={24} md={12} xl={12}>
                <Form.Item
                  name="CustomAmount"
                  label="Monthly Rate*"
                  colon={false}
                  rules={[hardwareValidation]}
                >
                  <AmountInput
                    placeholder="CustomAmount"
                    onChange={(value) =>
                      form.setFieldsValue({
                        CustomAmount: value,
                      })
                    }
                  />
                </Form.Item>
              </Col>
            )}
            <Divider />
            <Col xs={24} sm={24} md={12} xl={12}>
              <Form.Item
                name="MDMDeviceName"
                label="MDM Device Name"
                colon={false}
                rules={[hardwareValidation]}
              >
                <Input placeholder="Enter MDM Device Name" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={12} xl={12}>
              <Form.Item
                name="DeploymentDate"
                label="Deployment Date"
                colon={false}
                rules={[hardwareValidation]}
              >
                <DatePicker
                  format="MM-DD-YYYY"
                  className="t-w-full"
                  disabledDate={(d) => !d || d.isAfter(moment())}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={12} xl={12}>
              <Form.Item
                name="DeviceLocation"
                label="Device Location*"
                colon={false}
                rules={[hardwareValidation]}
              >
                <Input placeholder="Enter Device Location" />
              </Form.Item>
            </Col>
            <Divider />
            <Col span={24} className="text-right t-space-x-2">
              <Button
                type="secondary"
                className="px-25"
                onClick={() => history.goBack()}
              >
                Cancel
              </Button>
              <Button
                loading={addOrUpdate}
                type="primary"
                className="px-25"
                htmlType="submit"
              >
                {buttonText}
              </Button>
            </Col>
          </Row>
        </Form>
      </Skeleton>
    </Card>
  )
}

export { AddOrEditHardware }
